import React, { FunctionComponent } from 'react';
import { Link, LinkProps, useLocation } from 'react-router-dom';
import { cn } from '@companion-professional/webutils';
import { PermissionType } from '@companion-professional/dctypes';
import { useSetMobileMenuOpen } from '../../state/layout';
import { usePermissions } from '../../state/auth';

interface SideNavLinkProps extends LinkProps {
  // restrictToPermissions is a list of permissions the user must hold at least one of for the link to be displayed. If
  // restrictToPermissions is not provided, the link will be displayed for all users.
  restrictToPermissions?: PermissionType[];
}

// SideNavLink is a stylized Link component that is used in the SideNav component.
export const SideNavLink: FunctionComponent<SideNavLinkProps> = ({
  restrictToPermissions,
  children,
  className,
  onClick,
  ...props
}) => {
  const permissions = usePermissions();
  const location = useLocation();
  const isActive =
    (location.pathname === '/' && props.to === '/') ||
    (props.to !== '/' && location.pathname.startsWith(props?.to as string));
  const setMobileMenuOpen = useSetMobileMenuOpen();

  if (restrictToPermissions && !permissions.some((permission) => restrictToPermissions.includes(permission))) {
    return null;
  }

  return (
    <Link
      className={cn(
        [
          'flex',
          'flex-row',
          'gap-3',
          'items-center',
          'rounded-full',
          'mb-1',
          'px-4',
          'py-2.5',
          'text-base',
          'transition',
          'duration-200',
          '[&>svg]:h-5',
          '[&>svg]:w-5',
          {
            'bg-primary': isActive,
            'text-on-primary': isActive,
            'font-semibold': isActive,
            'text-on-nav/80': !isActive,
            'hover:bg-on-nav/10': !isActive,
            'hover:text-on-nav': !isActive
          }
        ],
        className
      )}
      onClick={(e) => {
        setMobileMenuOpen(false);
        onClick?.(e);
      }}
      {...props}
    >
      {children}
    </Link>
  );
};
